import {ElementCursor, ElementProperties, ElementStyle, ElementView} from '@app/modules/svg/element/ElementView';
import {ElementType} from '@app/modules/svg/dataSource/constant/ElementType';
import {Container} from '@app/modules/svg/Container';
import {Point} from '@app/modules/svg/model/Point';
import {PointedView} from '@app/modules/svg/element/shape/pointed/PointedView';
import {PathCommand} from '@app/modules/svg/model/path/PathCommand';
import {Style} from '@app/modules/svg/service/style/Style';
import {PathView} from '@app/modules/svg/element/shape/path/PathView';
import {Path} from '@app/modules/svg/model/path/Path';
import {MoveTo} from '@app/modules/svg/model/path/point/MoveTo';
import {fromJSON} from '@app/modules/svg/model/path/fromJSON';
import {pointsEqual} from '@app/modules/svg/service/math/Line';
import {LineTo} from '@app/modules/svg/model/path/line/LineTo';
import {Rect} from '@app/modules/svg/model/Rect';
import {Arc} from '@app/modules/svg/model/path/curve/arc/Arc';

export interface StyledEdge {
  point1: number;
  point2: number;
  command: PathCommand;
  style?: Style;
}

export class MeshStyle extends ElementStyle {
  protected override element: MeshView;

  public constructor(element: MeshView) {
    super(element);
    this.element = element;
  }

  public override get strokeWidth(): string {
    return super.strokeWidth;
  }
  public override set strokeWidth(width: string) {
    super.strokeWidth = width;
    this.element._styledEdges.forEach((styledEdge: StyledEdge) => {
      if (!styledEdge.style) {
        styledEdge.style = new Style();
      }

      const visibleDasharray = [];
      const realDasharray = styledEdge.style.strokeDashArray.split(' ');
      const oldStrokeWidth = parseInt(styledEdge.style.strokeWidth);
      realDasharray.forEach(dash => {
        const dashIntValue = parseInt(dash);
        visibleDasharray.push(dashIntValue / oldStrokeWidth * parseInt(width));
      });

      styledEdge.style.strokeDashArray = visibleDasharray.join(' ');
      styledEdge.style.strokeWidth = width;
    });

    this.element.__updateView__();
  }

  public override get strokeDashArray(): string {
    return super.strokeDashArray;
  }
  public override set strokeDashArray(array: string) {
    this.element._styledEdges.forEach((styledEdge: StyledEdge) => {
      if (!styledEdge.style) {
        styledEdge.style = new Style();
      }
      styledEdge.style.strokeDashArray = array;
    });

    this.element.__updateView__();
  }

  public override get strokeColor(): string {
    return super.strokeColor;
  }
  public override set strokeColor(color: string) {
    super.strokeColor = color;
    this.element._styledEdges.forEach((styledEdge: StyledEdge) => {
      if (!styledEdge.style) {
        styledEdge.style = new Style();
      }
      styledEdge.style.strokeColor = color;
    });

    this.element.__updateView__();
  }

  public override get fillColor(): string {
    return super.fillColor;
  }
  public override set fillColor(color: string) {
    super.fillColor = color;
    this.element._styledEdges.forEach((styledEdge: StyledEdge) => {
      if (!styledEdge.style) {
        styledEdge.style = new Style();
      }
      styledEdge.style.fillColor = color;
    });

    this.element.__updateView__();
  }

  public override get fontSize(): string {
    return super.fontSize;
  }
  public override set fontSize(size: string) {
    super.fontSize = size;
    this.element._styledEdges.forEach((styledEdge: StyledEdge) => {
      if (!styledEdge.style) {
        styledEdge.style = new Style();
      }
      styledEdge.style.fontSize = size;
    });

    this.element.__updateView__();
  }

  public override get fontColor(): string {
    return super.fontColor;
  }
  public override set fontColor(color: string) {
    super.fontColor = color;
    this.element._styledEdges.forEach((styledEdge: StyledEdge) => {
      if (!styledEdge.style) {
        styledEdge.style = new Style();
      }
      styledEdge.style.fontColor = color;
    });

    this.element.__updateView__();
  }

  public override get backgroundColor(): string {
    return super.backgroundColor;
  }
  public override set backgroundColor(color: string)  {
    super.backgroundColor = color;
    this.element._styledEdges.forEach((styledEdge: StyledEdge) => {
      if (!styledEdge.style) {
        styledEdge.style = new Style();
      }
      styledEdge.style.backgroundColor = color;
    });

    this.element.__updateView__();
  }
}

export interface MeshProperties extends ElementProperties {}

export abstract class MeshView extends PointedView {
  protected svgElement: SVGElement = document.createElementNS(ElementView.svgURI, 'g');
  protected _type: ElementType = ElementType.MESH;
  public override rotatable: boolean = true;
  public readonly style: MeshStyle = new MeshStyle(this);

  public _lastStyledEdges: StyledEdge[] = [];
  public _styledEdges: StyledEdge[] = [];

  public constructor(container: Container,
                     properties: MeshProperties = {},
                     points: Point[] = [],
                     styledEdges: StyledEdge[] = [],
                     ownerId?: string, index?: number) {

    super(container, ownerId, index);
    this.svgElement.id = this.id;
    this.points = points;
    this._styledEdges = styledEdges;

    this.__fixRect__();

    this.setProperties(properties);
  }

  public __updateView__(): void {
    this.svgElement.innerHTML = '';

    this._styledEdges.forEach((edge: StyledEdge): void => {
      edge.command.position = this._points[edge.point2];
      const line = new PathView(this._container, {overEvent: false}, new Path(
        [
          new MoveTo(this._points[edge.point1]),
          edge.command
        ]
      ));

      if (edge.style) {
        line.style.replace(edge.style);
      } else {
        line.style.replace(new Style());
      }

      this.svgElement.appendChild(line.SVG);
    });
  }

  public override get editablePoints(): Point[] {
    return this._points.map((point: Point) => {
      const curvedEdgeContainsPoint: boolean = !!this._styledEdges.find((styledEdge: StyledEdge) => {
        const pointInEdge: boolean = pointsEqual(this._points[styledEdge.point1], point) || pointsEqual(this._points[styledEdge.point2], point);
        const edgeIsCurved: boolean = !(styledEdge.command instanceof LineTo || styledEdge.command instanceof MoveTo);

        return pointInEdge && edgeIsCurved;
      });

      if (curvedEdgeContainsPoint) {
        return undefined;
      } else {
        return point;
      }
    });
  }

  public override __fixRect__(): void {
    super.__fixRect__();
    this._lastStyledEdges = structuredClone(this._styledEdges);
  }

  public override __setRect__(rect: Rect) {
    ElementView.normalizeRect(rect);

    let dw: number = 1;
    let dh: number = 1;

    if (this._lastRect.width !== 0) {
      dw = rect.width / (this._lastRect.width);
    }
    if (this._lastRect.height !== 0) {
      dh = rect.height / (this._lastRect.height);
    }

    for (let i: number = 0; i < this.points.length; i++) {
      /* points may not be fixed, and this._lastPoints[i] may be undefined */
      if (!this._lastPoints[i]) {
        this._lastPoints[i] = {x: 0, y: 0};
      }

      this.replacePoint(i, {
        x: (this._lastPoints[i].x - this._lastRect.x) * dw + rect.x,
        y: (this._lastPoints[i].y - this._lastRect.y) * dh + rect.y,
      });
    }

    this._rect = ElementView.calculateRect(this.points);

    this.__updateView__();
  }

  public override set cursor(cursor: string) {
    this.svgElement.style.cursor = cursor;
  }

  public isComplete(): boolean {
    return this._rect.width > 0 && this._rect.height > 0;
  }

  public override toJSON(): any {
    const json: any = super.toJSON();
    json.styledEdges = this._styledEdges;
    return json;
  }
  public override fromJSON(json: any): void {
    this._points = json.points;
    this._styledEdges = json.styledEdges;
    this._styledEdges.forEach((styledEdge: StyledEdge): void => {
      styledEdge.command = fromJSON(styledEdge.command.type, styledEdge.command);
    });

    this._rect = ElementView.calculateRect(this.points);

    this.setId(json.ownerId, json.index);
    this.__fixRect__();
    this.__rotate__(json.angle);
    this.refPoint = json.refPoint;
    this.setProperties(json.properties);
    this.rotatable = json.rotatable;
    this.erasable = json.erasable;

    this.__updateView__();
  };



  private static oppositeSigns(firstNumber: number, secondNumber: number): boolean {
    return (firstNumber < 0)? (secondNumber >= 0): (secondNumber < 0);
  }
}
